/**
 * Seals a support view off from the admin's own browser.
 *
 * WHY THIS EXISTS. Read-only protects the CUSTOMER'S stored data. It says
 * nothing about the visit contaminating the admin. The cart is the clearest
 * case: it lives in one store shared by every tab on this site, and the app
 * merges the signed-in account's server cart into it on load — so opening a
 * customer's account dropped their items into the admin's own cart, where they
 * stayed after the view ended and synced to the admin's account on the next
 * change. Dismissed banners, the cached account and per-account flags all live
 * in the same shared store and cross over the same way.
 *
 * WHY A SHIM RATHER THAN A LIST. Naming each key to isolate is the same mistake
 * as naming each endpoint to block: it holds until someone stores something new.
 * A viewing tab gets its own storage entirely, so anything written during the
 * visit — today's keys and tomorrow's — is written there and thrown away with
 * the tab.
 *
 * WHY IT RUNS FIRST. The Redux store reads the cart out of storage while its
 * module is still being imported, long before anything renders. This must be
 * imported ahead of the app or the swap comes too late to matter.
 */

const PREFIX = "as-view-store:";

const viewKeys = (): string[] => {
  const out: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(PREFIX)) out.push(k.slice(PREFIX.length));
  }
  return out;
};

/**
 * Backed by sessionStorage, which is already per-tab: a reload keeps the view's
 * own state, closing the tab discards it. Starts empty — the admin's storage is
 * never copied in, so nothing of theirs can be read or overwritten.
 */
const viewStorage: Storage = {
  get length() {
    return viewKeys().length;
  },
  key(index: number) {
    return viewKeys()[index] ?? null;
  },
  getItem(key: string) {
    return sessionStorage.getItem(PREFIX + key);
  },
  setItem(key: string, value: string) {
    sessionStorage.setItem(PREFIX + key, value);
  },
  removeItem(key: string) {
    sessionStorage.removeItem(PREFIX + key);
  },
  clear() {
    viewKeys().forEach((k) => sessionStorage.removeItem(PREFIX + k));
  },
};

try {
  // Either we are arriving with a handover code, or this tab already holds a
  // view. Both mean: everything from here on is the view's, not the admin's.
  const arriving = new URLSearchParams(window.location.search).has("view");
  const alreadyViewing = !!sessionStorage.getItem("as-view-token");
  if (arriving || alreadyViewing) {
    Object.defineProperty(window, "localStorage", {
      configurable: true,
      get: () => viewStorage,
    });
  }
} catch {
  /* A browser that refuses the swap keeps its own storage — the session is
     still read-only, so the worst case is the contamination we had before. */
}

export {};
